import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Box,
  TextField
} from '@mui/material';
import * as React from 'react';

interface TagInputsProps {
  options: Array<string>;
  disabled?: boolean;
  input_label?: string;
  tag_suggestions?: Set<string>;
  tag_limit?: number;
  handleChangeTags: (tags: Array<string>) => void;
}

/**
 * Tag Input component to allow multi chip inputs to a list with autocomplete suggestions.
 * @param {Array<string>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {Array<string>} props.tag_suggestion Array of suggested values for already used tags. Defaults to empty array.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {Array<ViewExtended>} props.handleChangeTags Callback function to notify the parent component when the selected options have changed.
 * @returns {JSX.Element}
 */

const TagInput = (props: TagInputsProps): JSX.Element => {
  const [currentNewTag, setCurrentNewTag] = React.useState<string>('');
  const [tags, setTags] = React.useState<Array<string>>(props.options);

  // Filter suggestions to exclude already selected tags
  const available_tags: Array<string> = Array.from(props.tag_suggestions || []).filter(
    (suggestion: string) => !tags.includes(suggestion)
  );

  const addTag = (new_tag: string): void => {
    if (new_tag.length > 0 && !tags.includes(new_tag)) {
      const updated_tags: Array<string> = [...tags, new_tag];
      setTags(updated_tags);
      props.handleChangeTags(updated_tags);
    }
    setCurrentNewTag('');
  };

  return (
    <Box>
      <Autocomplete
        disableCloseOnSelect
        freeSolo
        multiple={true}
        options={available_tags}
        limitTags={props.tag_limit}
        value={tags}
        size="small"
        inputValue={currentNewTag}
        onInputChange={(
          event: React.SyntheticEvent<Element, Event>,
          newInputValue: string,
          reason: AutocompleteInputChangeReason
        ) => {
          if (reason === 'clear') {
            setTags([]);
          } else {
            setCurrentNewTag(newInputValue);
          }
        }}
        onChange={(event: React.SyntheticEvent<Element, Event>, new_value: Array<string>) => {
          setTags(new_value);
          props.handleChangeTags(new_value);
        }}
        onBlur={() => {
          if (currentNewTag.length > 0) {
            if (tags.indexOf(currentNewTag) === -1) {
              setTags([...tags, currentNewTag]);
              props.handleChangeTags([...tags, currentNewTag]);
            }
            setCurrentNewTag('');
          }
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
          if ((event.key === 'Enter' || event.key === 'Tab') && currentNewTag.length > 0) {
            event.preventDefault();
            addTag(currentNewTag);
          }
        }}
        disabled={props.disabled}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <>
            <TextField
              {...params}
              variant="standard"
              fullWidth
              label={props.input_label ?? 'Tags'}
            />
          </>
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
          <li {...props}>{option}</li>
        )}
      />
    </Box>
  );
};

export default TagInput;
